import React, { useState, useEffect, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { MapPin, FileText, Shield, Menu, X, LogOut, Camera, Plus, Locate } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { CAMERA_TYPES } from '../utils/cameraTypes';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function HomePage() {
  const { user, login, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [showAddCamera, setShowAddCamera] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  // Auth form
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    subscribe_newsletter: false,
    accept_terms: false
  });

  // Camera form
  const [cameraForm, setCameraForm] = useState({
    latitude: '',
    longitude: '',
    address: '',
    camera_type: '',
    installation_date: '',
    photo: null
  });

  useEffect(() => {
    loadCameras();
  }, []);

  const loadCameras = async () => {
    try {
      const res = await axios.get(`${API}/cameras`);
      setCameras(res.data);
    } catch (error) {
      console.error('Failed to load cameras:', error);
    }
  };

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setCameraForm(prev => ({
          ...prev,
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6)
        }));

        // Reverse geocoding using Nominatim (OpenStreetMap)
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );
          const data = await res.json();
          if (data.display_name) {
            setCameraForm(prev => ({
              ...prev,
              address: data.display_name
            }));
            toast.success('Location detected and address filled!');
          }
        } catch (error) {
          toast.success('Location detected! Please enter the address manually.');
        }
        setLocationLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Unable to get your location. Please enter manually.');
        setLocationLoading(false);
      }
    );
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    // Validation for registration
    if (!isLogin) {
      if (!authForm.accept_terms) {
        toast.error('You must accept the terms and privacy policy');
        return;
      }
      if (!authForm.full_name.trim()) {
        toast.error('Full name is required');
        return;
      }
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: authForm.email, password: authForm.password }
        : authForm;
      
      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.access_token, res.data.user);
      setShowAuth(false);
      setAuthForm({
        email: '',
        password: '',
        full_name: '',
        phone_number: '',
        subscribe_newsletter: false,
        accept_terms: false
      });
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleAddCamera = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('latitude', cameraForm.latitude);
    formData.append('longitude', cameraForm.longitude);
    formData.append('address', cameraForm.address);
    formData.append('camera_type', cameraForm.camera_type);
    formData.append('installation_date', cameraForm.installation_date);
    if (cameraForm.photo) {
      formData.append('photo', cameraForm.photo);
    }

    try {
      await axios.post(`${API}/cameras`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Camera location added successfully!');
      setShowAddCamera(false);
      setCameraForm({
        latitude: '',
        longitude: '',
        address: '',
        camera_type: '',
        installation_date: '',
        photo: null
      });
      loadCameras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add camera');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Camera className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-slate-800">FlockTracker</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-slate-700 hover:text-blue-600 font-medium transition-colors">
                Map
              </Link>
              <Link to="/stories" className="text-slate-700 hover:text-blue-600 font-medium transition-colors">
                Stories
              </Link>
              {user?.role === 'moderator' && (
                <Link to="/moderator" className="text-slate-700 hover:text-blue-600 font-medium transition-colors flex items-center space-x-1">
                  <Shield className="w-4 h-4" />
                  <span>Moderator</span>
                </Link>
              )}
              {user ? (
                <div className="flex items-center space-x-4">
                  <span className="text-slate-600">{user.email}</span>
                  <Button onClick={logout} variant="outline" size="sm" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setShowAuth(true)} data-testid="login-btn">Login / Sign Up</Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-slate-700"
              onClick={() => setMobileMenu(!mobileMenu)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-3">
              <Link to="/" className="block text-slate-700 hover:text-blue-600 font-medium">
                Map
              </Link>
              <Link to="/stories" className="block text-slate-700 hover:text-blue-600 font-medium">
                Stories
              </Link>
              {user?.role === 'moderator' && (
                <Link to="/moderator" className="block text-slate-700 hover:text-blue-600 font-medium">
                  Moderator Dashboard
                </Link>
              )}
              {user ? (
                <div className="space-y-2">
                  <div className="text-slate-600">{user.email}</div>
                  <Button onClick={logout} variant="outline" size="sm" className="w-full">
                    Logout
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setShowAuth(true)} className="w-full">Login / Sign Up</Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-4">
            Track Flock Cameras <br />in Your Community
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Help your community stay informed about surveillance camera locations. Share stories, experiences, and contribute to privacy awareness.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {user && (
              <Button 
                onClick={() => setShowAddCamera(true)} 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-camera-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Report Camera Location
              </Button>
            )}
            <Button 
              onClick={() => navigate('/stories')} 
              size="lg" 
              variant="outline"
              data-testid="view-stories-btn"
            >
              <FileText className="w-5 h-5 mr-2" />
              View Stories
            </Button>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <Card className="p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-800">Camera Locations</h2>
            </div>
            <div className="text-sm text-slate-600">
              {cameras.length} {cameras.length === 1 ? 'camera' : 'cameras'} reported
            </div>
          </div>
          <div className="h-[500px] rounded-lg overflow-hidden" data-testid="map-container">
            <MapContainer center={[37.7749, -122.4194]} zoom={10} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {cameras.map((camera) => (
                <Marker key={camera.id} position={[camera.latitude, camera.longitude]}>
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-semibold text-slate-800 mb-1">{camera.camera_type}</h3>
                      <p className="text-sm text-slate-600 mb-1">{camera.address}</p>
                      <p className="text-xs text-slate-500">Reported: {camera.installation_date}</p>
                      <p className="text-xs text-slate-500">By: {camera.reported_by_email}</p>
                      {camera.photo_url && (
                        <img src={camera.photo_url} alt="Camera" className="mt-2 w-full h-32 object-cover rounded" />
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Card>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle>{isLogin ? 'Login' : 'Create Account'}</DialogTitle>
            <DialogDescription>
              {isLogin ? 'Enter your credentials to access your account' : 'Create a new account to start contributing'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                required
              />
            </div>
            
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    data-testid="full-name-input"
                    value={authForm.full_name}
                    onChange={(e) => setAuthForm({ ...authForm, full_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone_number">Phone Number (Optional)</Label>
                  <Input
                    id="phone_number"
                    type="tel"
                    data-testid="phone-input"
                    value={authForm.phone_number}
                    onChange={(e) => setAuthForm({ ...authForm, phone_number: e.target.value })}
                  />
                </div>
              </>
            )}
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                required
              />
            </div>

            {!isLogin && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="newsletter"
                    checked={authForm.subscribe_newsletter}
                    onCheckedChange={(checked) => setAuthForm({ ...authForm, subscribe_newsletter: checked })}
                    data-testid="newsletter-checkbox"
                  />
                  <label htmlFor="newsletter" className="text-sm text-slate-700">
                    Subscribe to newsletters and updates
                  </label>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={authForm.accept_terms}
                    onCheckedChange={(checked) => setAuthForm({ ...authForm, accept_terms: checked })}
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-700">
                    I accept the{' '}
                    <Link to="/terms" target="_blank" className="text-blue-600 hover:underline">
                      Terms of Use
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </>
            )}
            
            <Button type="submit" className="w-full" data-testid="auth-submit-btn">
              {isLogin ? 'Login' : 'Sign Up'}
            </Button>
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setAuthForm({
                  email: '',
                  password: '',
                  full_name: '',
                  phone_number: '',
                  subscribe_newsletter: false,
                  accept_terms: false
                });
              }}
              className="w-full text-sm text-blue-600 hover:underline"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Camera Dialog */}
      <Dialog open={showAddCamera} onOpenChange={setShowAddCamera}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-camera-dialog">
          <DialogHeader>
            <DialogTitle>Report Camera Location</DialogTitle>
            <DialogDescription>
              Submit details about a flock camera in your area to help the community stay informed
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCamera} className="space-y-4">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={getUserLocation}
                disabled={locationLoading}
                data-testid="detect-location-btn"
              >
                <Locate className="w-4 h-4 mr-2" />
                {locationLoading ? 'Detecting...' : 'Detect My Location'}
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  data-testid="latitude-input"
                  value={cameraForm.latitude}
                  onChange={(e) => setCameraForm({ ...cameraForm, latitude: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  data-testid="longitude-input"
                  value={cameraForm.longitude}
                  onChange={(e) => setCameraForm({ ...cameraForm, longitude: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                data-testid="address-input"
                value={cameraForm.address}
                onChange={(e) => setCameraForm({ ...cameraForm, address: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="camera_type">Camera Type</Label>
              <Select 
                value={cameraForm.camera_type} 
                onValueChange={(value) => setCameraForm({ ...cameraForm, camera_type: value })}
              >
                <SelectTrigger data-testid="camera-type-select">
                  <SelectValue placeholder="Select camera type" />
                </SelectTrigger>
                <SelectContent>
                  {CAMERA_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="installation_date">Installation/Reported Date</Label>
              <Input
                id="installation_date"
                type="date"
                data-testid="installation-date-input"
                value={cameraForm.installation_date}
                onChange={(e) => setCameraForm({ ...cameraForm, installation_date: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="photo">Photo (optional)</Label>
              <Input
                id="photo"
                type="file"
                accept="image/*"
                data-testid="photo-input"
                onChange={(e) => setCameraForm({ ...cameraForm, photo: e.target.files[0] })}
              />
            </div>
            
            <Button type="submit" className="w-full" data-testid="submit-camera-btn">
              Submit Camera Location
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}